import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Video, ShoppingBag, Mic, TrendingUp, Megaphone, ArrowRight, Gift, Sparkles, Target, Radio, BarChart3, ChevronLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const INTERESTS = [
  { key: 'create', icon: Video, label: 'Create & Go Live', desc: 'Make music, go live, earn tips', color: 'from-emerald-500/20 to-emerald-600/5', border: 'border-emerald-500/30', text: 'text-emerald-400' },
  { key: 'sell', icon: ShoppingBag, label: 'Sell Products', desc: 'List and sell anything', color: 'from-violet-500/20 to-violet-600/5', border: 'border-violet-500/30', text: 'text-violet-400' },
  { key: 'invest', icon: TrendingUp, label: 'Back Talent', desc: 'Invest in rising stars from $1', color: 'from-amber-500/20 to-amber-600/5', border: 'border-amber-500/30', text: 'text-amber-400' },
  { key: 'market', icon: Megaphone, label: 'Market & Grow', desc: 'AI campaigns, email, analytics', color: 'from-cyan-500/20 to-cyan-600/5', border: 'border-cyan-500/30', text: 'text-cyan-400' },
  { key: 'shop', icon: Target, label: 'Shop & Discover', desc: 'Browse deals, attend events', color: 'from-rose-500/20 to-rose-600/5', border: 'border-rose-500/30', text: 'text-rose-400' },
];

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const [step, setStep] = useState('form'); // 'form' | 'interests'
  const [interests, setInterests] = useState([]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [referralCode, setReferralCode] = useState(searchParams.get('ref') || '');
  const creatorRef = searchParams.get('creator_ref') || '';
  const [loading, setLoading] = useState(false);

  const toggleInterest = (key) => {
    setInterests(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) { toast.error('All fields required'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setStep('interests');
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      await register(name, email, password, 'member', referralCode || null, creatorRef || null);
      toast.success('Welcome to MMarketing!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4 py-8" data-testid="register-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-10 h-10 bg-gradient-to-br from-[#b8ff00] to-[#00ff88] rounded-xl flex items-center justify-center">
            <span className="font-display font-black text-xl text-black">M</span>
          </div>
          <span className="font-display font-bold text-xl text-white">MMarketing</span>
        </Link>

        <AnimatePresence mode="wait">
          {step === 'form' && (
            <motion.div key="form" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
                <h1 className="font-display font-bold text-2xl text-white mb-1">Join MMarketing</h1>
                <p className="text-sm text-zinc-500 mb-6">One account. Every capability. No limits.</p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label className="text-zinc-400 text-xs">Name</Label>
                    <Input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="register-name" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Email</Label>
                    <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@example.com" className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="register-email" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Password</Label>
                    <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="6+ characters" className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="register-password" />
                  </div>
                  {referralCode && (
                    <div className="flex items-center gap-2 p-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                      <Gift size={14} className="text-emerald-400" />
                      <span className="text-xs text-emerald-400">Referral: {referralCode} (+100 bonus points)</span>
                    </div>
                  )}
                  <Button type="submit" className="w-full bg-[#b8ff00] text-black font-bold py-5 text-sm" data-testid="register-continue-btn">
                    Continue <ArrowRight size={14} className="ml-2" />
                  </Button>
                </form>

                <p className="text-center text-xs text-zinc-600 mt-4">
                  Already have an account? <Link to="/login" className="text-[#b8ff00] font-bold hover:underline">Sign In</Link>
                </p>
              </div>
            </motion.div>
          )}

          {step === 'interests' && (
            <motion.div key="interests" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}>
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
                <button onClick={() => setStep('form')} className="text-zinc-500 hover:text-white text-xs flex items-center gap-1 mb-4">
                  <ChevronLeft size={12} /> Back
                </button>
                <h1 className="font-display font-bold text-2xl text-white mb-1">What excites you?</h1>
                <p className="text-sm text-zinc-500 mb-6">Pick as many as you want. You can do ALL of this — we'll just personalize your starting view.</p>

                <div className="space-y-2 mb-6">
                  {INTERESTS.map(item => {
                    const selected = interests.includes(item.key);
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.key}
                        onClick={() => toggleInterest(item.key)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                          selected
                            ? `bg-gradient-to-r ${item.color} ${item.border} ring-1 ring-white/10`
                            : 'bg-zinc-800/50 border-zinc-700/50 hover:border-zinc-600'
                        }`}
                        data-testid={`interest-${item.key}`}
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${selected ? item.text + ' bg-white/5' : 'bg-zinc-700 text-zinc-400'}`}>
                          <Icon size={16} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-bold ${selected ? 'text-white' : 'text-zinc-300'}`}>{item.label}</p>
                          <p className="text-[10px] text-zinc-500">{item.desc}</p>
                        </div>
                        {selected && <span className="text-[#b8ff00] text-lg">+</span>}
                      </button>
                    );
                  })}
                </div>

                <Button onClick={handleComplete} disabled={loading} className="w-full bg-[#b8ff00] text-black font-bold py-5 text-sm" data-testid="register-complete-btn">
                  {loading ? 'Creating...' : interests.length === 0 ? 'Skip & Explore Everything' : `Let's Go (${interests.length} selected)`}
                </Button>

                <p className="text-center text-[10px] text-zinc-600 mt-3">
                  Don't worry — nothing is locked. You can always access every feature.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
